import React, { forwardRef, useCallback } from 'react';
import { FieldError } from 'react-hook-form';

import { DropdownSmall } from '@rover/icons';
import { Flex, Text } from '@rover/kibble/core';
import { useTheme } from '@rover/kibble/themes';

import { getVariantStyles, SelectFieldOptionConfig } from './SelectField.common';

export type { SelectFieldOptionConfig as Option };

type SelectFieldInputProps = {
  placeholder: string;
  selectedOption: SelectFieldOptionConfig | null;
  id: string;
  menuId: string;
  primaryLabel: string;
  secondaryLabel?: string;
  isOpen: boolean;
  fieldError?: FieldError;
  fieldErrorId: string;
  toggleOpen: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  width?: string;
  disabled?: boolean;
};

const truncateStyles = {
  'white-space': 'nowrap',
  overflow: 'hidden',
  'text-overflow': 'ellipsis',
};

const SelectFieldInput = forwardRef<HTMLElement, SelectFieldInputProps>(
  (
    {
      placeholder,
      selectedOption,
      isOpen,
      id,
      menuId,
      primaryLabel,
      secondaryLabel,
      fieldError,
      fieldErrorId,
      toggleOpen,
      onKeyDown: handleInputKeyDown,
      width = '100%',
      disabled,
    },
    ref
  ) => {
    const theme = useTheme();
    const { colors } = theme;

    const accessibleSecondaryLabel = secondaryLabel ? ` - ${secondaryLabel}` : '';
    const accessibleLabel = selectedOption
      ? `${primaryLabel}${accessibleSecondaryLabel} - Selected: ${selectedOption.label}`
      : `${primaryLabel}${accessibleSecondaryLabel} - ${placeholder}`;

    const hasError: boolean = !!fieldError;

    const variantStyles = getVariantStyles(hasError, !!selectedOption, disabled);

    const handleFieldClick = useCallback((): void => {
      if (!disabled) {
        toggleOpen();
      }
    }, [disabled, toggleOpen]);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>): void => {
        if (!disabled) {
          handleInputKeyDown(event);
        }
      },
      [disabled, handleInputKeyDown]
    );

    return (
      // eslint-disable-next-line jsx-a11y/role-supports-aria-props
      <Flex
        data-testid="select-field-input"
        role="button"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={menuId}
        aria-label={accessibleLabel}
        aria-describedby={fieldError ? fieldErrorId : undefined}
        aria-disabled={disabled}
        aria-invalid={hasError}
        htmlRef={ref}
        width={width}
        border="2px solid"
        borderRadius="4px"
        borderColor={variantStyles.borderColor}
        padding="3x"
        value={selectedOption?.value}
        id={id}
        tabIndex={0}
        onClick={handleFieldClick}
        onMouseDown={(e) => e.currentTarget.focus()}
        onKeyDown={handleKeyDown}
        height="48px"
        alignItems="center"
        background={variantStyles.backgroundColor}
        sx={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          userSelect: 'none',
          '&:focus': {
            outline: `2px solid ${colors.border.input_focus}`,
          },
          marginBottom: '2px',
        }}
      >
        <Flex flexGrow="1" mr="2x">
          {selectedOption ? (
            <Text textColor={variantStyles.textColor} sx={truncateStyles}>
              {selectedOption?.label}
            </Text>
          ) : (
            <Text textColor="tertiary" sx={truncateStyles}>
              {placeholder}
            </Text>
          )}
        </Flex>
        <DropdownSmall
          height="24px"
          width="24px"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
            fill: disabled
              ? colors.interactive_text.disabled_base.toString()
              : colors.interactive_text.primary_base.toString(),
          }}
        />
      </Flex>
    );
  }
);

export default SelectFieldInput;
